import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * Desktop breakpoint used by the announcement split layout CSS.
   * @type {MediaQueryList}
   */
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('click', this.#handleClick);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktopMediaQuery.addEventListener('change', this.#handleDesktopMediaQueryChange);

    this.play();
    this.#syncSlides();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktopMediaQuery.removeEventListener('change', this.#handleDesktopMediaQueryChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Selects a specific announcement.
   * @param {number} index
   */
  select(index) {
    this.current = index;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;
    this.#syncSlides();
  }

  /**
   * Syncs visible slides. Desktop split mode shows announcements in pairs.
   */
  #syncSlides() {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    const visibleIndices = this.#visibleSlideIndices(slides.length);
    const visibleIndexSet = new Set(visibleIndices);

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${!visibleIndexSet.has(index)}`);
    });

    this.refs.dots?.forEach((dot, index) => {
      if (index === visibleIndices[0]) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.#syncCurrentSlideColors(slides[visibleIndices[0]], visibleIndices.length);
  }

  /**
   * Gets the visible slide indexes for the current mode.
   * @param {number} slideCount
   * @returns {number[]}
   */
  #visibleSlideIndices(slideCount) {
    if (this.#isSplitDesktop) {
      const pageCount = Math.ceil(slideCount / 2);
      const pageIndex = this.#normalizeIndex(this.current, pageCount);
      const firstIndex = pageIndex * 2;

      return [firstIndex, firstIndex + 1].filter((index) => index < slideCount);
    }

    return [this.#normalizeIndex(this.current, slideCount)];
  }

  /**
   * @param {number} index
   * @param {number} count
   * @returns {number}
   */
  #normalizeIndex(index, count) {
    let relativeIndex = index % count;

    if (relativeIndex < 0) {
      relativeIndex += count;
    }

    return relativeIndex;
  }

  get #isSplitDesktop() {
    return this.closest('.announcement-bar')?.classList.contains('announcement-bar--split-desktop') && this.#desktopMediaQuery.matches;
  }

  /**
   * Keeps the bar background/text color in sync with the visible slide in slider mode.
   * @param {HTMLElement} currentSlide
   * @param {number} visibleSlideCount
   */
  #syncCurrentSlideColors(currentSlide, visibleSlideCount = 1) {
    const bar = this.closest('.announcement-bar');
    const background = currentSlide?.dataset.announcementBackground;
    const color = currentSlide?.dataset.announcementColor;

    bar?.style.setProperty('--announcement-visible-count', `${visibleSlideCount}`);

    if (background) {
      bar?.style.setProperty('--announcement-current-background', background);
    } else {
      bar?.style.removeProperty('--announcement-current-background');
    }

    if (color) {
      bar?.style.setProperty('--announcement-current-color', color);
    } else {
      bar?.style.removeProperty('--announcement-current-color');
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #handleDesktopMediaQueryChange = () => this.#syncSlides();

  /**
   * Handles dot pagination clicks.
   * @param {Event} event
   */
  #handleClick = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('[data-announcement-dot-index]') : null;
    if (!(dot instanceof HTMLElement)) return;

    const index = Number.parseInt(dot.dataset.announcementDotIndex ?? '', 10);
    if (Number.isNaN(index)) return;

    this.select(index);
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
